
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sortOptions } from "@/data/mockData";
import { Category, SortOption } from "@/lib/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Search } from "lucide-react";

type CategoryFilterProps = {
  selectedCategory: Category;
  onSelectCategory: (category: Category) => void;
  selectedSort: SortOption;
  onSelectSort: (sort: SortOption) => void;
};

export function CategoryFilter({
  selectedCategory,
  onSelectCategory,
  selectedSort,
  onSelectSort,
}: CategoryFilterProps) {
  const [searchQuery, setSearchQuery] = useState("");
  
  // All available categories with emojis
  const allCategories = [
    { value: 'all', label: '🌐 All', emoji: '🌐' },
    // Creative & Visual
    { value: 'ai-art', label: '🎨 AI Art', emoji: '🎨' },
    { value: 'anime', label: '🎌 Anime', emoji: '🎌' },
    { value: '3d-renders', label: '🎲 3D Renders', emoji: '🎲' },
    { value: 'character-design', label: '👤 Character Design', emoji: '👤' },
    { value: 'image-editing', label: '✂️ Image Editing', emoji: '✂️' },
    { value: 'concept-art', label: '🖼️ Concept Art', emoji: '🖼️' },
    { value: 'ai-edits', label: '✨ AI Edits', emoji: '✨' },
    { value: 'aesthetic-photography', label: '📸 Aesthetic Photography', emoji: '📸' },
    // Writing & Storytelling
    { value: 'story-scriptwriting', label: '📖 Story & Scriptwriting', emoji: '📖' },
    { value: 'poetry', label: '✍️ Poetry', emoji: '✍️' },
    { value: 'blogging', label: '📝 Blogging', emoji: '📝' },
    { value: 'copywriting', label: '✏️ Copywriting', emoji: '✏️' },
    { value: 'marketing-content', label: '📢 Marketing Content', emoji: '📢' },
    { value: 'social-media-posts', label: '📱 Social Media Posts', emoji: '📱' },
    { value: 'trendy-ai-posts', label: '🔥 Trendy AI Posts', emoji: '🔥' },
    { value: 'dialogue-prompts', label: '💬 Dialogue Prompts', emoji: '💬' },
    // AI & Tech
    { value: 'stable-diffusion', label: '🌊 Stable Diffusion', emoji: '🌊' },
    { value: 'midjourney', label: '🚀 Midjourney', emoji: '🚀' },
    { value: 'chatgpt', label: '🤖 ChatGPT', emoji: '🤖' },
    { value: 'gemini', label: '♊ Gemini', emoji: '♊' },
    { value: 'claude', label: '🧠 Claude', emoji: '🧠' },
    { value: 'ai-ml-finetuning', label: '⚙️ AI/ML Fine-Tuning', emoji: '⚙️' },
    { value: 'prompt-engineering', label: '🔧 Prompt Engineering', emoji: '🔧' },
    { value: 'api-automation', label: '🔗 API & Automation', emoji: '🔗' },
    // Professional & Business
    { value: 'hr-recruitment', label: '👔 HR & Recruitment', emoji: '👔' },
    { value: 'management-operations', label: '📊 Management & Operations', emoji: '📊' },
    { value: 'product-management', label: '📦 Product Management', emoji: '📦' },
    { value: 'design-thinking', label: '💡 Design Thinking', emoji: '💡' },
    { value: 'business-strategy', label: '📈 Business Strategy', emoji: '📈' },
    { value: 'branding-identity', label: '🎯 Branding & Identity', emoji: '🎯' },
    { value: 'productivity-prompts', label: '⚡ Productivity Prompts', emoji: '⚡' },
    // Education & Research
    { value: 'study-aids', label: '📚 Study Aids', emoji: '📚' },
    { value: 'data-science-engineering', label: '🔬 Data Science & Engineering', emoji: '🔬' },
    { value: 'coding-development', label: '💻 Coding & Development', emoji: '💻' },
    { value: 'research-assistance', label: '🔍 Research Assistance', emoji: '🔍' },
    { value: 'ai-learning-resources', label: '🎓 AI Learning Resources', emoji: '🎓' },
    { value: 'educational-games', label: '🎮 Educational Games', emoji: '🎮' },
    // Entertainment & Media
    { value: 'gaming', label: '🎮 Gaming', emoji: '🎮' },
    { value: 'movie-concepts', label: '🎬 Movie Concepts', emoji: '🎬' },
    { value: 'meme-creation', label: '😂 Meme Creation', emoji: '😂' },
    { value: 'animation-ideas', label: '🎞️ Animation Ideas', emoji: '🎞️' },
    { value: 'music-prompts', label: '🎵 Music Prompts', emoji: '🎵' },
    // Others / Daily Life
    { value: 'personal-development', label: '🌱 Personal Development', emoji: '🌱' },
    { value: 'motivation', label: '💪 Motivation', emoji: '💪' },
    { value: 'mental-health', label: '🧘 Mental Health', emoji: '🧘' },
    { value: 'relationship-prompts', label: '❤️ Relationship Prompts', emoji: '❤️' },
    { value: 'travel-lifestyle', label: '✈️ Travel & Lifestyle', emoji: '✈️' },
    { value: 'food-recipes', label: '🍳 Food & Recipes', emoji: '🍳' },
    { value: 'general-chat', label: '💭 General Chat', emoji: '💭' },
  ];

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return allCategories;
    
    const query = searchQuery.toLowerCase();
    return allCategories.filter(cat => 
      cat.label.toLowerCase().includes(query) || 
      cat.value.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Extended sort options
  const extendedSortOptions = [
    { value: 'following', label: '👥 Following' },
    { value: 'latest', label: 'Latest' },
    { value: 'popular', label: 'Popular' },
    { value: 'trending', label: 'Trending' },
    { value: 'most_liked', label: 'Most Liked' },
    { value: 'most_commented', label: 'Most Commented' },
    { value: 'newest', label: 'Newest' }
  ];

  // Function to get the current sort label
  const getCurrentSortLabel = () => {
    const option = extendedSortOptions.find((option) => option.value === selectedSort);
    return option ? option.label : "Sort";
  };

  // Handle category selection
  const handleCategoryClick = (categoryValue: string) => {
    console.log("Category clicked:", categoryValue);
    onSelectCategory(categoryValue as Category);
  };

  return (
    <div className="w-full space-y-4">
      {/* Search Bar */}
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-background/50 border-border/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
        />
      </div>

      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between">
        <div className="w-full md:flex-1 md:mr-4">
          {/* Categories with smooth horizontal scroll */}
          <div className="relative">
            <div 
              className="flex items-center gap-2 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'hsl(var(--border)) transparent'
              }}
            >
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <Button
                    key={category.value}
                    variant={selectedCategory === category.value ? "default" : "outline"}
                    size="sm"
                    className="whitespace-nowrap text-xs md:text-sm px-3 py-2 md:px-4 md:py-2 flex-shrink-0 snap-start transition-all hover:scale-105"
                    onClick={() => handleCategoryClick(category.value)}
                  >
                    {category.label}
                  </Button>
                ))
              ) : (
                <div className="w-full text-center py-4 text-muted-foreground text-sm">
                  No categories found 👀
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-center md:justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center space-x-1 text-xs md:text-sm px-3 py-2"
              >
                <span>{getCurrentSortLabel()}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[120px]">
              {extendedSortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => onSelectSort(option.value as SortOption)}
                  className={selectedSort === option.value ? "bg-secondary" : ""}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
